
import { useState, FormEvent } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>(
    'idle'
  );

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    // Basic client‑side validation
    if (
      !formData.name ||
      !formData.phone ||
      !formData.email ||
      !formData.message
    ) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    if (formData.message.length > 500) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('phone', formData.phone);
      formBody.append('email', formData.email);
      formBody.append('message', formData.message);

      const response = await fetch(
        'https://readdy.ai/api/form/d6e9abiohb161tfd8up0',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formBody.toString(),
        }
      );

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', phone: '', email: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="bg-[#F5F7FA] py-16 sm:py-24 lg:py-32"
    >
      <div className="max-w-3xl mx-auto px-4 sm:px-8">
        {/* Section Header */}
        <div className="text-center mb-8 sm:mb-12">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-2 h-2 rounded-full bg-[#0B1F3A]"></div>
            <span
              className="text-[#0B1F3A] text-xs font-semibold tracking-widest uppercase"
              style={{ fontFamily: 'Open Sans, sans-serif' }}
            >
              Get In Touch
            </span>
          </div>
          <h2
            className="text-[#0B1F3A] text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 sm:mb-8"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Let&apos;s Start Your Project
          </h2>

          {/* Contact Info Bar */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-12 mb-8 sm:mb-12">
            <a href="tel:316-867-6848" className="flex items-center gap-3 group cursor-pointer">
              <div className="w-10 h-10 rounded-full bg-[#2D6CDF]/10 flex items-center justify-center group-hover:bg-[#2D6CDF] transition-colors">
                <i className="ri-phone-line text-[#2D6CDF] text-lg group-hover:text-white transition-colors"></i>
              </div>
              <span
                className="text-[#0B1F3A] text-sm sm:text-base font-medium"
                style={{ fontFamily: 'Open Sans, sans-serif' }}
              >
                316-867-6848
              </span>
            </a>
            <a href="mailto:info@premier-ict.com" className="flex items-center gap-3 group cursor-pointer">
              <div className="w-10 h-10 rounded-full bg-[#2D6CDF]/10 flex items-center justify-center group-hover:bg-[#2D6CDF] transition-colors">
                <i className="ri-mail-line text-[#2D6CDF] text-lg group-hover:text-white transition-colors"></i>
              </div>
              <span
                className="text-[#0B1F3A] text-sm sm:text-base font-medium"
                style={{ fontFamily: 'Open Sans, sans-serif' }}
              >
                info@premier-ict.com
              </span>
            </a>
          </div>
        </div>

        {/* Form Container */}
        <div className="bg-white rounded-3xl p-6 sm:p-8 lg:p-12 shadow-lg">
          <form
            id="contact-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="space-y-5 sm:space-y-6"
          >
            <div>
              <input
                type="text"
                name="name"
                placeholder="Your Name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className="w-full px-4 sm:px-6 py-3.5 sm:py-4 border border-gray-200 rounded-lg text-[#1A1A1A] placeholder:text-[#1A1A1A]/40 focus:outline-none focus:border-[#2D6CDF] transition-colors text-sm sm:text-base"
                style={{ fontFamily: 'Open Sans, sans-serif' }}
                required
              />
            </div>

            <div>
              <input
                type="tel"
                name="phone"
                placeholder="Phone Number"
                value={formData.phone}
                onChange={(e) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
                className="w-full px-4 sm:px-6 py-3.5 sm:py-4 border border-gray-200 rounded-lg text-[#1A1A1A] placeholder:text-[#1A1A1A]/40 focus:outline-none focus:border-[#2D6CDF] transition-colors text-sm sm:text-base"
                style={{ fontFamily: 'Open Sans, sans-serif' }}
                required
              />
            </div>

            <div>
              <input
                type="email"
                name="email"
                placeholder="Email Address"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                className="w-full px-4 sm:px-6 py-3.5 sm:py-4 border border-gray-200 rounded-lg text-[#1A1A1A] placeholder:text-[#1A1A1A]/40 focus:outline-none focus:border-[#2D6CDF] transition-colors text-sm sm:text-base"
                style={{ fontFamily: 'Open Sans, sans-serif' }}
                required
              />
            </div>

            <div>
              <textarea
                name="message"
                placeholder="Tell us about your project..."
                value={formData.message}
                onChange={(e) =>
                  setFormData({ ...formData, message: e.target.value })
                }
                maxLength={500}
                rows={5}
                className="w-full px-4 sm:px-6 py-3.5 sm:py-4 border border-gray-200 rounded-lg text-[#1A1A1A] placeholder:text-[#1A1A1A]/40 focus:outline-none focus:border-[#2D6CDF] transition-colors resize-none text-sm sm:text-base"
                style={{ fontFamily: 'Open Sans, sans-serif' }}
                required
              ></textarea>
              <div
                className="text-right text-xs sm:text-sm text-[#1A1A1A]/40 mt-2"
                style={{ fontFamily: 'Open Sans, sans-serif' }}
              >
                {formData.message.length}/500
              </div>
            </div>

            {submitStatus === 'success' && (
              <div
                className="bg-green-50 border border-green-200 text-green-700 px-4 sm:px-6 py-3 sm:py-4 rounded-lg text-center text-sm sm:text-base"
                style={{ fontFamily: 'Open Sans, sans-serif' }}
              >
                Thank you! We&apos;ll contact you soon.
              </div>
            )}

            {submitStatus === 'error' && (
              <div
                className="bg-red-50 border border-red-200 text-red-700 px-4 sm:px-6 py-3 sm:py-4 rounded-lg text-center text-sm sm:text-base"
                style={{ fontFamily: 'Open Sans, sans-serif' }}
              >
                Please check all fields and try again.
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#0B1F3A] text-white py-4 sm:py-5 rounded-full text-base sm:text-lg font-semibold hover:bg-[#2D6CDF] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              style={{ fontFamily: 'Open Sans, sans-serif' }}
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
